import eventBus from 'widgets/toolbox/eventBus';

const TOKEN_EXPIRE_TIME = 55 * 60 * 1000; // 55 minutes

/**
 * @category widgets
 * @subcategory toolbox
 * @module token
 * @description Module represents functionality to get/set token name and value
 *
 * @example <caption>Example of token updating from some module</caption>
 * import token from 'widgets/toolbox/token';
 * ...
 * return submitFormJson(this.prefs().sessionContinueUrl, {}).then((response) => {
 *     token.name = response.csrf.tokenName;
 *     token.value = response.csrf.token;
 *     token.timestamp = response.csrf.timestamp;
 *     this.closeModal();
 *     this.countdownShown = false;
 * });
 * ...
 */
const CSRFToken = {
    tokenName: window.csrfToken?.name || '',
    tokenValue: window.csrfToken?.value || '',
    tokenTimestamp: window.csrfToken?.timestamp || Date.now(),

    /**
     * @description Token name
     */
    get name(): string {
        if (!this.tokenName) {
            this.tokenName = window.csrfToken?.name || '';
        }

        return this.tokenName;
    },

    set name(value: string) {
        this.tokenName = value;
    },

    /**
     * @description Token value
     */
    get value(): string {
        if (!this.tokenValue) {
            this.tokenValue = window.csrfToken?.value || '';
        }

        return this.tokenValue;
    },

    set value(value: string) {
        eventBus.emit('broadcast.send', {
            type: 'csrfUpdate',
            value,
            timestamp: this.timestamp
        });

        this.tokenValue = value;
    },

    /**
     * @description Token timestamp
     */
    get timestamp(): number {
        if (!this.tokenTimestamp) {
            this.tokenTimestamp = window.csrfToken?.timestamp || Date.now();
        }

        return this.tokenTimestamp;
    },

    set timestamp(value: number) {
        this.tokenTimestamp = value;
    },

    isExpired(): boolean {
        return Date.now() - this.timestamp > TOKEN_EXPIRE_TIME;
    }
};

/**
 * @description Listen to broadcast message from service worker and update token value
 * when it was changed in the other tab
 * @param {object} message - message from service worker
 * @param {string} message.type - message type
 * @param {string} message.value - new token value
 */
eventBus.on('broadcast.message', (message) => {
    if (message.type === 'csrfUpdate') {
        CSRFToken.tokenValue = message.value;
        CSRFToken.tokenTimestamp = message.timestamp;
    }
});

/**
 * @description Send token value to service worker when it was initialized
 * to update token value in other tabs
 * @event broadcast.inited
 */
eventBus.on('broadcast.inited', () => {
    eventBus.emit('broadcast.send', {
        type: 'csrfUpdate',
        value: CSRFToken.value,
        timestamp: CSRFToken.timestamp
    });
});

export default CSRFToken;
